import { ChevronDown } from 'lucide-preact'
import { useSettingIds } from './setting-row.tsx'
import styles from './select.module.css'

interface Choice<T extends string> {
  readonly value: T
  readonly label: string
}

interface SelectProps<T extends string> {
  readonly value: T
  readonly options: readonly Choice<T>[]
  readonly onChange: (value: T) => void
}

/** A native select. Left and right cycle the options, which is what a gamepad sends. */
export function Select<T extends string>({ value, options, onChange }: SelectProps<T>) {
  const { id, describedBy } = useSettingIds()

  const cycle = (delta: number): void => {
    const index = options.findIndex((option) => option.value === value)
    const next = options[(index + delta + options.length) % options.length]
    if (next) onChange(next.value)
  }

  return (
    <div class={styles.wrap}>
      <select
        id={id}
        class={styles.select}
        value={value}
        aria-describedby={describedBy}
        onChange={(event) => {
          const next = options.find((option) => option.value === event.currentTarget.value)
          if (next) onChange(next.value)
        }}
        onKeyDown={(event) => {
          const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
          if (delta === 0) return
          event.preventDefault()
          cycle(delta)
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown class={styles.chevron} aria-hidden="true" />
    </div>
  )
}
